import React, { useState } from 'react';
import { ArrowRightIcon, TrashIcon } from '../icons';
import { Modal } from './Modal';
import { Invitee } from '../../types';
import { apiService } from '../../services/apiService';
import { friendlyAuthError } from '../../utils/authErrors';
import { InlineAlert } from '../InlineAlert';

interface InviteModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const FormInput = ({ label, placeholder, value, onChange }: { label: string; placeholder: string; value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void }) => (
    <div className="flex flex-col items-start gap-1.5 flex-1">
        <label className="text-grayscale-1200 text-xs font-grotesk">{label}</label>
        <input 
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            className="flex h-11 py-3 px-2.5 items-center gap-2.5 self-stretch rounded-md bg-grayscale-200 text-grayscale-1100 font-grotesk text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
    </div>
);

export const InviteModal: React.FC<InviteModalProps> = ({ isOpen, onClose }) => {
    const [invitees, setInvitees] = useState<Invitee[]>([
        { id: 1, email: '', firstName: '', lastName: '', orgName: '', linkedin: '' }
    ]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    
    const handleAddInvitee = () => {
        setInvitees(prev => [...prev, { id: Date.now(), email: '', firstName: '', lastName: '', orgName: '', linkedin: '' }]);
    };

    const handleRemoveInvitee = (id: number) => {
        setInvitees(prev => prev.filter(invitee => invitee.id !== id));
    };

    const handleInviteeChange = (id: number, field: keyof Omit<Invitee, 'id'>, value: string) => {
        setInvitees(prev => prev.map(invitee => invitee.id === id ? { ...invitee, [field]: value } : invitee));
    };

    const handleSendInvites = async () => {
        if (isSubmitting) return;

        // Validate that all invitees have at least an email
        const validInvitees = invitees.filter(invitee => invitee.email.trim());
        if (validInvitees.length === 0) {
            setErrorMessage('Please add at least one valid email address.');
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus('idle');
        setErrorMessage('');

        try {
            const userId = apiService.getStoredUserId();
            if (!userId) {
                setErrorMessage('User session not found. Please log in again.');
                setIsSubmitting(false);
                return;
            }

            // Send invites sequentially to handle multiple invites
            const results = [];
            for (const invitee of validInvitees) {
                try {
                    const response = await apiService.sendReferralInvite(userId, {
                        email: invitee.email,
                        first_name: invitee.firstName || 'Guest',
                        last_name: invitee.lastName || 'User',
                        country: 'Not specified',
                        organisation: invitee.orgName || 'Not specified',
                        designation: 'Not specified',
                        linkedin_url: invitee.linkedin || undefined
                    });
                    
                    if (response.success) {
                        results.push({ email: invitee.email, success: true });
                    } else if (response.user_exists && response.action === 'login_required') {
                        // User already exists
                        results.push({ 
                            email: invitee.email, 
                            success: false, 
                            error: 'User already exists - they should login instead' 
                        });
                    } else {
                        results.push({ email: invitee.email, success: false, error: response.error });
                    }
                } catch (error) {
                    results.push({ email: invitee.email, success: false, error: 'Network error' });
                }
            }

            // Check results
            const successCount = results.filter(r => r.success).length;
            const failedResults = results.filter(r => !r.success);

            if (successCount > 0) {
                setSubmitStatus('success');
                if (failedResults.length === 0) {
                    // All successful - close modal after a moment
                    setTimeout(() => {
                        onClose();
                        // Reset form
                        setInvitees([{ id: 1, email: '', firstName: '', lastName: '', orgName: '', linkedin: '' }]);
                        setSubmitStatus('idle');
                        setErrorMessage('');
                    }, 2000);
                } else {
                    // Some failed - show specific error messages
                    const failedMessages = failedResults.map(r => `${r.email}: ${r.error}`).join('\n');
                    setErrorMessage(`${successCount} invite(s) sent successfully.\n\nFailed invites:\n${failedMessages}`);
                }
            } else {
                // All failed - show specific error messages  
                setSubmitStatus('error');
                if (failedResults.length === 1) {
                    // Single invite failed - show the specific error
                    setErrorMessage(friendlyAuthError(failedResults[0].error, 'Failed to send invite. Please try again.'));
                } else {
                    // Multiple invites failed - show all errors
                    const failedMessages = failedResults.map(r => `${r.email}: ${r.error}`).join('\n');
                    setErrorMessage(`Failed to send invites:\n${failedMessages}`);
                }
            }
        } catch (error) {
            setSubmitStatus('error');
            setErrorMessage('Network error. Please check your connection and try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Invite">
            <div className="flex flex-col gap-4 max-h-[80vh] pt-4">
                <h2 className="text-grayscale-1300 font-grotesk text-base font-bold">Invite</h2>
                <p className="text-grayscale-900 font-grotesk text-sm">Find us interesting and helpful? Invite your friends, colleagues, or team to join us!</p>
                
                <div className="overflow-y-auto hide-scrollbar pr-2 -mr-2 px-1">
                    {invitees.map((invitee, index) => (
                        <div key={invitee.id}>
                            <hr className="border-primary-300 my-4" />
                            <div className="flex flex-col gap-4 px-2 py-2">
                                <div className="flex items-center gap-4">
                                    <div className="flex-1">
                                        <FormInput label="Email:" placeholder="Enter email address" value={invitee.email} onChange={(e) => handleInviteeChange(invitee.id, 'email', e.target.value)} />
                                    </div>
                                    {invitees.length > 1 && <TrashIcon onClick={() => handleRemoveInvitee(invitee.id)} />}
                                </div>
                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                                    <FormInput label="First Name:" placeholder="Add first name" value={invitee.firstName} onChange={(e) => handleInviteeChange(invitee.id, 'firstName', e.target.value)} />
                                    <FormInput label="Last Name:" placeholder="Add last name" value={invitee.lastName} onChange={(e) => handleInviteeChange(invitee.id, 'lastName', e.target.value)} />
                                </div>
                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                                    <FormInput label="Organisation:" placeholder="Add organisation's name" value={invitee.orgName} onChange={(e) => handleInviteeChange(invitee.id, 'orgName', e.target.value)} />
                                    <FormInput label="LinkedIn or alternate URL:" placeholder="Add link here" value={invitee.linkedin} onChange={(e) => handleInviteeChange(invitee.id, 'linkedin', e.target.value)} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <hr className="border-primary-300 mx-2" />
                
                <div className="flex justify-end px-2">
                    <button onClick={handleAddInvitee} className="flex items-center gap-3 text-grayscale-1300 font-grotesk text-sm font-bold">
                        <span>Add an Invite</span>
                    </button>
                </div>
                
                {/* Status Messages */}
                <div className="px-2">
                    {submitStatus === 'success' && (
                        <InlineAlert variant="success" className="mt-4">
                            Invites sent successfully! 🎉
                        </InlineAlert>
                    )}
                    {(submitStatus === 'error' || errorMessage) && (
                        <InlineAlert variant="error" className="mt-4">
                            {errorMessage.split('\n').map((line, index) => (
                                <p key={index} className={index === 0 ? 'font-medium' : 'mt-1'}>{line}</p>
                            ))}
                        </InlineAlert>
                    )}
                </div>
                
                <div className="flex flex-col items-center gap-2 self-stretch px-2 pb-2">
                     <button 
                        onClick={handleSendInvites}
                        disabled={isSubmitting || submitStatus === 'success'}
                        className="flex py-2.5 px-4 justify-center items-center gap-2 self-stretch rounded-full bg-primary-500 shadow-custom-light text-grayscale-100 font-grotesk text-sm font-medium disabled:bg-primary-300 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? 'Sending...' : submitStatus === 'success' ? 'Sent!' : 'Send Invites'} 
                        {!isSubmitting && <ArrowRightIcon />}
                    </button>
                </div>
            </div>
        </Modal>
    );
};
